import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { extractHomeAwayScores } from '~shared/components/score-board/utils';
import NormalizeStrategies from '~shared/components/score-board/normalizers';
import classNames from 'classnames';
import { Link } from '~spa/Components/Link/Link';
import { seoUrlEvent, seoUrlInplayEvent } from '~spa/Utils/SeoUrls';
import { getHasBoostedOddsSelections } from '~shared/utils/sportsbook';
import EventSchedule from '~shared/components/react/EventSchedule/';
import EventName from '~shared/components/react/EventName';
import EventScore from '~shared/components/react/EventScore';
import EventIcons from '~shared/components/react/EventIcons';
import '~shared/components/react/Scoreboard/styles/scoreboard-wrapper.scss';
import './styles.scss';

const EventDescription = (props) => {
    const {
        gtm = '',
        hasBoostedOddsSelections = null,
        isDateHidden = false,
        isLink = true,
        event: {
            id: eventId,
            sport_slug,
            category_slug,
            isUsFormat = false,
            event_url: eventUrl,
            // we get both (from WS & XHR response)
            inplay = false,
            is_inplay = false,
            usesThreeMarketView = false,
            competitors: _competitors = [],
            tournament_logos: tournamentLogos = {},
            is_streaming_available: isStreamingAvailable = false,
            start_time,
            market_id,
            markets,
        },
    } = props;

    const isInplay = inplay || is_inplay;
    // This is to account for 'competitors' sometimes being an object and sometimes
    // being an array depending on the endpoint.
    // if no order key then returns the same array of objects
    const competitors = Object.values(_competitors)
        .sort(({ order: orderA }, { order: orderB }) => (orderA - orderB));
    const normalize = NormalizeStrategies[sport_slug];

    const {
        status = '',
        columns = [],
    } = (isInplay && normalize?.(props.event)) || {};

    const getLinkUrl = () => {
        return isInplay ? seoUrlInplayEvent(eventUrl, eventId) : seoUrlEvent(eventUrl, eventId);
    };

    const iconClassName = classNames({ 'event-icons--icon-arrow': isLink });

    const scores = extractHomeAwayScores(columns);

    const shouldShowTeamLogo = Boolean(tournamentLogos?.showTeamLogo);

    let hasBoostedOdds = hasBoostedOddsSelections;
    if (hasBoostedOddsSelections === null && markets && markets[market_id]) {
        hasBoostedOdds = getHasBoostedOddsSelections(markets[market_id]?.selections);
    }

    const gridTemplate = classNames(
        {'min-content': shouldShowTeamLogo},
        '1fr',
        {[`repeat(${scores.home.length}, min-content)`]: scores.home.length},
    );

    const gridStyle = {
        gridTemplateColumns: gridTemplate,
    };

    const renderEventDescription = () => {
        const events = competitors.map(({ slug, name }, index) => {
            const isHome = index === 0;

            const competitorScores = isHome
                ? scores.home
                : scores.away;

            return (
                <Fragment key={index}>
                    <EventName
                        shouldShowTeamLogo={shouldShowTeamLogo}
                        categorySlug={category_slug}
                        name={name}
                        sportSlug={sport_slug}
                        slug={slug}
                        isUsFormat={isUsFormat}
                        isHome={isHome}
                    />
                    <EventScore scores={competitorScores} />
                </Fragment>
            );
        });

        if (isUsFormat) {
            return events.reverse();
        }

        return events;
    };


    const mainClassNames = classNames('event-description', {
        'event-description--inplay': isInplay,
        'event-description--three-market': usesThreeMarketView,
    });

    const content = <>
        <div className='event-description__grid' style={gridStyle}>
            {renderEventDescription()}
        </div>
        <div className='event-description__footer'>
            <span className='event-description__status'>
                {isInplay
                    ? status
                    : <EventSchedule
                        startTime={start_time}
                        isInplay={isInplay}
                        isDateHidden={isDateHidden}
                        showDateInline
                        showGreyTimer
                    />
                }
            </span>
            <EventIcons
                showBoostedIcons={hasBoostedOdds}
                showStreaming={isStreamingAvailable}
                extraClassname={iconClassName}
            />
        </div>
    </>;

    return isLink
        ? <Link className={mainClassNames} to={getLinkUrl()} gtmAttribute={gtm}>
            {content}
        </Link>
        : <div className={mainClassNames}>
            {content}
        </div>;
};

EventDescription.propTypes = {
    event: PropTypes.object.isRequired,
    isDateHidden: PropTypes.bool,
    isLink: PropTypes.bool,
    hasBoostedOddsSelections: PropTypes.oneOf([true, false, null]),
    gtm: PropTypes.string,
};

export default EventDescription;
